import {
  Document,
  Font,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import AmiriRegular from "../../../fonts/Amiri-Regular.ttf";

import moment from "moment";
import { decodeUnicode } from "./utils";

Font.register({
  family: "Amiri",
  src: AmiriRegular, // ✅ full Arabic font
});

const ClaimGssReportPdf = ({ printData, claimFilters, printName }) => {
  const selectedBranch = JSON.parse(localStorage.getItem("selectedBranch"));

  const totalClaims = printData?.gss_rprt?.reduce(
    (acc, row) => acc + parseInt(row.total_claims, 10),
    0,
  );
  const batchNumbers = printData?.batch_no
    ? printData.batch_no.split(",")[0].trim()
    : "";

  const tableData = printData?.gss_rprt || [];
  const totalGross = tableData
    ?.reduce((acc, row) => acc + parseFloat(row.tot_grossamnt), 0)
    .toFixed(2);
  const totalDeductible = tableData
    ?.reduce((acc, row) => acc + parseFloat(row.tot_patshreamnt), 0)
    .toFixed(2);
  const totalDiscount = tableData
    ?.reduce((acc, row) => acc + parseFloat(row.tot_discamnt), 0)
    .toFixed(2);
  const totalNet = tableData
    ?.reduce((acc, row) => acc + parseFloat(row.tot_netamnt), 0)
    .toFixed(2);
  const totalVat = tableData
    ?.reduce((acc, row) => acc + parseFloat(row.tot_vatamnt), 0)
    .toFixed(2);
  const totalNetWithVat = tableData
    ?.reduce((acc, row) => acc + parseFloat(row.tot_netvatamount), 0)
    .toFixed(2);

  return (
    <Document>
      <Page size="A3" style={styles.page}>
        <Text style={styles.heading}>
          {claimFilters?.branch?.branch_name || selectedBranch?.branch_name}
        </Text>

        {/* Provider details */}
        <View style={styles.section}>
          <Text style={styles.item}>
            <Text style={styles.label}>CHI No : </Text>
            <Text style={styles.value}>
              {claimFilters?.branch_cchino ||
                selectedBranch?.branch_cchi ||
                selectedBranch?.branch_cchino}
            </Text>
          </Text>
          <Text style={styles.item}>
            <Text style={styles.label}>NPHIES ID : </Text>
            <Text style={styles.value}>{printData?.nphies_code || ""}</Text>
          </Text>
          <Text style={styles.item}>
            <Text style={styles.label}>Provider Code : </Text>
            <Text style={styles.value}>{printData?.provider_code || ""}</Text>
          </Text>
          <Text style={styles.item}>
            <Text style={styles.label}>City : </Text>
            <Text style={styles.value}>{printData?.branch_city || ""}</Text>
          </Text>
          <Text style={styles.item}>
            <Text style={styles.label}>Provider : </Text>
            <Text style={styles.value}>
              {claimFilters?.branch?.branch_name ||
                selectedBranch?.branch_name ||
                ""}
            </Text>
          </Text>

          <Text style={styles.item}>
            <Text style={styles.label}>CR Number : </Text>
            <Text style={styles.value}>{printData?.branch_crno || ""}</Text>
          </Text>
          <Text style={styles.item}>
            <Text style={styles.label}>VAT Number : </Text>
            <Text style={styles.value}>{printData?.branch_vatno || ""}</Text>
          </Text>
        </View>

        <Text style={styles.heading}>General SUMMARY Statment</Text>

        {/* Summary Row */}
        <View style={[styles.row, { flexWrap: "wrap" }]}>
          <Text style={styles.rowItem}>
            <Text style={styles.label}>For the month of: </Text>
            <Text style={styles.value}>
              {claimFilters?.start_date &&
              claimFilters?.end_date &&
              moment(claimFilters.start_date).isSame(
                claimFilters.end_date,
                "month",
              )
                ? moment(claimFilters.start_date).format("MMMM/YYYY")
                : ""}
            </Text>
          </Text>

          <Text style={styles.rowItem}>
            <Text style={styles.label}>Payer Name: </Text>
            <Text style={[styles.value, styles.colored]}>
              {printData?.insurance_name}
            </Text>
          </Text>
          <Text style={styles.rowItem}>
            <Text style={styles.label}>Payer Vat: </Text>
            <Text style={[styles.value, styles.colored]}>
              {printData?.insurance_vatno || ""}
            </Text>
          </Text>

          <Text style={styles.rowItem}>
            <Text style={styles.label}>Batch No: </Text>
            <Text style={[styles.value, { gap: 8 }]}>{batchNumbers}</Text>
          </Text>
        </View>

        {/* Table */}
        <View style={styles.table}>
          {/* Table Header */}
          <View style={styles.tableRowHeader}>
            <View style={[styles.tableCol, { flex: 0.5 }]}>
              <Text style={styles.tableHeaderCell}>NO.</Text>
            </View>
            <View style={[styles.tableCol, { flex: 1.5 }]}>
              <Text style={styles.tableHeaderCell}>Policy No.</Text>
            </View>
            <View style={[styles.tableCol, { flex: 3 }]}>
              <Text style={styles.tableHeaderCell}>Policy Holder Name</Text>
            </View>
            <View style={[styles.tableCol, { flex: 1 }]}>
              <Text style={styles.tableHeaderCell}>No of Claims</Text>
            </View>
            <View style={[styles.tableCol, { flex: 1.2 }]}>
              <Text style={styles.tableHeaderCell}>Gross</Text>
            </View>
            <View style={[styles.tableCol, { flex: 1.2 }]}>
              <Text style={styles.tableHeaderCell}>Deductible</Text>
            </View>
            <View style={[styles.tableCol, { flex: 1.2 }]}>
              <Text style={styles.tableHeaderCell}>Discount</Text>
            </View>
            <View style={[styles.tableCol, { flex: 1.2 }]}>
              <Text style={styles.tableHeaderCell}>Net</Text>
            </View>
            <View style={[styles.tableCol, { flex: 1.2 }]}>
              <Text style={styles.tableHeaderCell}>VAT</Text>
            </View>
            <View style={[styles.tableCol, { flex: 1.2 }]}>
              <Text style={styles.tableHeaderCell}>Net with VAT</Text>
            </View>
          </View>

          {/* Table Rows */}
          {tableData.map((row, idx) => {
            const decoded = row?.policy_name?.[0]?.includes("\\u")
              ? decodeUnicode(row.policy_name[0])
              : row?.policy_name?.[0];

            return (
              <View key={idx} style={styles.tableRow}>
                <View style={[styles.tableCol, { flex: 0.5 }]}>
                  <Text style={[styles.cellText, { textAlign: "center" }]}>
                    {idx + 1}
                  </Text>
                </View>

                <View style={[styles.tableCol, { flex: 1.5 }]}>
                  <Text style={[styles.cellText, { textAlign: "center" }]}>
                    {row.policy_no}
                  </Text>
                </View>

                <View style={[styles.tableCol, { flex: 3 }]}>
                  <Text style={[styles.cellText, { textAlign: "center" }]}>
                    {decoded}
                  </Text>
                </View>

                <View style={[styles.tableCol, { flex: 1 }]}>
                  <Text style={[styles.cellText, { textAlign: "center" }]}>
                    {row.total_claims}
                  </Text>
                </View>

                <View style={[styles.tableCol, { flex: 1.2 }]}>
                  <Text style={[styles.cellText, { textAlign: "center" }]}>
                    {Number(row.tot_grossamnt).toFixed(2)}
                  </Text>
                </View>

                <View style={[styles.tableCol, { flex: 1.2 }]}>
                  <Text style={[styles.cellText, { textAlign: "center" }]}>
                    {Number(row.tot_patshreamnt).toFixed(2)}
                  </Text>
                </View>

                <View style={[styles.tableCol, { flex: 1.2 }]}>
                  <Text style={[styles.cellText, { textAlign: "center" }]}>
                    {Number(row.tot_discamnt).toFixed(2)}
                  </Text>
                </View>

                <View style={[styles.tableCol, { flex: 1.2 }]}>
                  <Text style={[styles.cellText, { textAlign: "center" }]}>
                    {Number(row.tot_netamnt).toFixed(2)}
                  </Text>
                </View>

                <View style={[styles.tableCol, { flex: 1.2 }]}>
                  <Text style={[styles.cellText, { textAlign: "center" }]}>
                    {Number(row.tot_vatamnt).toFixed(2)}
                  </Text>
                </View>

                <View style={[styles.tableCol, { flex: 1.2 }]}>
                  <Text style={[styles.cellText, { textAlign: "center" }]}>
                    {Number(row.tot_netvatamount).toFixed(2)}
                  </Text>
                </View>
              </View>
            );
          })}

          {/* Total Row */}
          <View style={styles.tableRow}>
            <View style={[styles.tableCol, { flex: 5.35 }]}>
              <Text style={styles.totalCell}>Grand Total</Text>
            </View>
            <View style={[styles.tableCol, { flex: 1 }]}>
              <Text style={styles.totalCell}>{totalClaims}</Text>
            </View>
            <View style={[styles.tableCol, { flex: 1.2 }]}>
              <Text style={styles.totalCell}>{totalGross}</Text>
            </View>
            <View style={[styles.tableCol, { flex: 1.2 }]}>
              <Text style={styles.totalCell}>{totalDeductible}</Text>
            </View>
            <View style={[styles.tableCol, { flex: 1.2 }]}>
              <Text style={styles.totalCell}>{totalDiscount}</Text>
            </View>
            <View style={[styles.tableCol, { flex: 1.2 }]}>
              <Text style={styles.totalCell}>{totalNet}</Text>
            </View>
            <View style={[styles.tableCol, { flex: 1.2 }]}>
              <Text style={styles.totalCell}>{totalVat}</Text>
            </View>
            <View style={[styles.tableCol, { flex: 1.2 }]}>
              <Text style={styles.totalCell}>{totalNetWithVat}</Text>
            </View>
          </View>
        </View>
        <Text style={styles.createdBy} fixed>
          {`Printed by: | |${printName} in  ${moment().format("lll")}`}
        </Text>
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `Page ${pageNumber || ""} of ${totalPages || ""}`
          }
          fixed
        />
      </Page>
    </Document>
  );
};

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 9,
    lineHeight: 1.4,
    backgroundColor: "#fff",
    fontFamily: "Amiri",
  },
  colored: {
    color: "#0dcaf0",
  },
  heading: {
    fontSize: 16,
    textAlign: "center",
    textTransform: "uppercase",
    fontWeight: 900,
    marginBottom: 12,
  },
  section: {
    marginVertical: 16,
    gap: 6,
  },
  label: {
    fontWeight: 700,
    fontSize: 12,
  },
  value: {
    fontSize: 12,
    fontWeight: 500,
  },
  item: {
    marginBottom: 4,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginVertical: 8,
  },
  rowItem: {
    fontSize: 12,
    marginBottom: 12,
  },

  // Table Styles
  table: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: "#000",
  },
  tableRowHeader: {
    flexDirection: "row",
    backgroundColor: "#4d4d4d",
    color: "#fff",
  },
  tableRow: {
    flexDirection: "row",
    borderTopWidth: 1,
    borderColor: "#000",
  },
  tableCol: {
    borderRightWidth: 1,
    borderColor: "#000",
    padding: 4,
  },
  tableHeaderCell: {
    fontWeight: 700,
    fontSize: 10,
    textAlign: "center",
    color: "#fff",
  },
  totalCell: {
    fontWeight: 700,
    fontSize: 10,
    textAlign: "center",
    overflowWrap: "break-word",
  },
  cellText: {
    fontSize: 10,
    overflowWrap: "break-word",
  },
  createdBy: {
    position: "absolute",
    fontSize: 8,
    bottom: 5,
    left: 10,
    textAlign: "left",
    color: "#a4a4a4",
  },
  pageNumber: {
    position: "absolute",
    fontSize: 8,
    bottom: 5,
    right: 10,
    textAlign: "right",
    color: "#a4a4a4",
  },
});

export default ClaimGssReportPdf;
